import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Card,
  TextField,
  InputAdornment,
  Icon,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Table, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "context/AuthContext";

// Custom status badge renderer
const renderStatusBadge = (status, isMobile) => {
  const config = {
    "To do": { label: "🔴 Task: To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
    "In progress": { label: "🟢 Task: In progress", color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" },
    "Hold": { label: "⏸ Task: Hold", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    "Clean": { label: "✅ Task: Clean", color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  }[status] || { label: "🔴 Task: To do", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1.5px solid ${config.border}`,
        fontWeight: "bold",
        fontSize: isMobile ? "0.65rem" : "0.72rem",
        borderRadius: "8px",
        px: isMobile ? 0.5 : 1,
      }}
    />
  );
};

// Format ISO timestamp to show only the time portion (excluding date)
const formatTimestamp = (isoStr) => {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  } catch (err) {
    return isoStr;
  }
};

// Format ISO timestamp to show date and time portion (Pakistan timezone)
const formatFullDateTime = (isoStr) => {
  if (!isoStr) return "N/A";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  } catch (err) {
    return isoStr;
  }
};

// Helper to clean trailing Reservation ID or extra bracketed IDs from room names
const cleanRoomName = (roomName) => {
  if (!roomName) return "";
  return roomName.replace(/\s+\([^\)]+\)$/, "").trim();
};

// Helper to extract a sortable timestamp from an apartment object
const getTaskSortTime = (apt) => {
  if (!apt || typeof apt !== "object") return 0;

  const checkoutStr = apt.actualCheckout;
  if (checkoutStr && checkoutStr.trim() !== "" && checkoutStr.trim().toLowerCase() !== "n/a") {
    const clean = checkoutStr.trim();
    const directParsed = Date.parse(clean);
    if (!isNaN(directParsed)) return directParsed;

    const timeMatch = clean.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const ampm = timeMatch[4] ? timeMatch[4].toUpperCase() : null;
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      return hours * 3600 + minutes * 60;
    }
  }

  const timestampStr = apt.timestampToDo || apt.timestamp;
  if (timestampStr) {
    const parsed = Date.parse(timestampStr);
    if (!isNaN(parsed)) return parsed;
  }

  if (apt.reservationId) {
    const resNum = parseInt(apt.reservationId, 10);
    if (!isNaN(resNum)) return resNum;
  }

  return 0;
};

// Helper to extract clean formatted time (e.g. "11:00 AM")
const getTaskFormattedTime = (apt) => {
  if (!apt || typeof apt !== "object") return "";
  const checkoutStr = apt.actualCheckout;
  if (checkoutStr && checkoutStr.trim() !== "" && checkoutStr.trim().toLowerCase() !== "n/a") {
    const clean = checkoutStr.trim();
    const timeMatch = clean.match(/(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      const timePart = timeMatch[1];
      const ampm = timeMatch[2] ? ` ${timeMatch[2].toUpperCase()}` : "";
      return `${timePart}${ampm}`;
    }
  }
  return "";
};

// Compute same day checkout labels: returns a Map { aptObject -> { label, num, time } }
const computeSameDayCheckoutLabels = (allApts) => {
  const groups = {};
  allApts.forEach((apt) => {
    if (!apt || typeof apt !== "object") return;
    const name = apt.name;
    if (!name) return;
    const base = cleanRoomName(name).toLowerCase().trim();
    if (!base) return;
    if (!groups[base]) groups[base] = [];
    groups[base].push(apt);
  });

  const labelMap = new Map();

  Object.values(groups).forEach((taskList) => {
    if (taskList.length > 1) {
      const sorted = [...taskList].sort((a, b) => getTaskSortTime(a) - getTaskSortTime(b));
      sorted.forEach((apt, idx) => {
        const num = idx + 1;
        const labelText = `Checkout ${num}`;
        labelMap.set(apt, { label: labelText, num });
      });
    }
  });

  return labelMap;
};


// Get today's date string in Pakistan timezone (YYYY-MM-DD)
const getPakistanDateString = (dateObj = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    return formatter.format(dateObj); // returns "YYYY-MM-DD"
  } catch (err) {
    return "";
  }
};

// Check if a date/timestamp is today in Pakistan timezone
const isTodayInPakistan = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return false;
  const clean = dateStr.trim();
  if (!clean || clean.toLowerCase() === "n/a") return false;

  try {
    const todayPk = getPakistanDateString(); // "YYYY-MM-DD"
    let taskPk = "";

    if (clean.includes("/")) {
      const datePart = clean.split(",")[0].trim();
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        const year = parts[2];
        taskPk = `${year}-${month}-${day}`;
      }
    }

    if (!taskPk) {
      const d = new Date(clean);
      if (!isNaN(d.getTime())) {
        taskPk = getPakistanDateString(d);
      }
    }

    return todayPk === taskPk;
  } catch (err) {
    return false;
  }
};

function CleaningStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname === "/cleaning" ? 0 : location.pathname === "/cleaning-status" ? 1 : 2;

  const { user, isFullAdmin, isCustom, hasPermission } = useAuth();
  const canEdit = isFullAdmin() || (isCustom() && hasPermission("cleaning", "complete"));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const cellPadding = isMobile ? "8px 6px" : isTablet ? "10px 8px" : "14px 12px";
  const headerPadding = isMobile ? "8px 6px" : isTablet ? "10px 8px" : "12px";
  const fontSize = isMobile ? "0.75rem" : isTablet ? "0.8rem" : "0.85rem";

  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Add Task State
  const [allApartments, setAllApartments] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [openAddTask, setOpenAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskAgent, setNewTaskAgent] = useState("");
  const [newTaskStatus, setNewTaskStatus] = useState("To do");
  const [submittingTask, setSubmittingTask] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  const [currentDialogTime, setCurrentDialogTime] = useState("");

  const getPakistanTimeString = () => {
    return new Date().toLocaleTimeString("en-US", {
      timeZone: "Asia/Karachi",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    if (openAddTask) {
      setCurrentDialogTime(getPakistanTimeString());
      const interval = setInterval(() => {
        setCurrentDialogTime(getPakistanTimeString());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [openAddTask]);

  const fetchApartments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/cleaning", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch apartment details");
      const result = await response.json();
      if (result.success && result.data) {
        setApartments(result.data);
        setAllApartments(result.allApartments || []);
      } else {
        setApartments([]);
        setAllApartments([]);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await fetch("/api/cleaning/agents", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setAgentsList(data.data);
        }
      }
    } catch (err) {
      console.error("Failed to fetch agents", err);
    }
  };

  useEffect(() => {
    fetchApartments();
    fetchAgents();
  }, []);

  const handleAddTaskSubmit = async () => {
    if (!newTaskName.trim()) {
      setNotification({
        open: true,
        message: "Please enter a room name",
        severity: "warning",
      });
      return;
    }

    const exists = apartments.some((apt) => {
      if (typeof apt !== "object") return false;
      if (apt.name.toLowerCase() !== newTaskName.trim().toLowerCase()) return false;
      const savedCol = localStorage.getItem(`cleaning_kanban_column_${apt.name}`);
      const dbCol = apt.column || "";
      return savedCol || dbCol;
    });
    if (exists) {
      setNotification({
        open: true,
        message: `A room named "${newTaskName.trim()}" already exists!`,
        severity: "warning",
      });
      return;
    }

    try {
      setSubmittingTask(true);

      const name = newTaskName.trim();
      localStorage.setItem(`cleaning_kanban_column_${name}`, newTaskStatus);
      if (newTaskAgent) {
        localStorage.setItem(`cleaning_kanban_agent_${name}`, newTaskAgent);
      } else {
        localStorage.removeItem(`cleaning_kanban_agent_${name}`);
      }

      if (newTaskStatus === "In progress") {
        localStorage.setItem(`cleaning_kanban_timer_${name}`, String(Date.now()));
      } else {
        localStorage.removeItem(`cleaning_kanban_timer_${name}`);
      }

      localStorage.setItem(`cleaning_kanban_accumulated_${name}`, "0");
      const now = new Date();
      const formattedTimestamp = now.toLocaleTimeString("en-US", {
        timeZone: 'Asia/Karachi',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      localStorage.setItem(`cleaning_button_logs_${name}`, JSON.stringify([{ state: "To do", timestamp: formattedTimestamp }]));

      const formattedDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      const body = {
        apartmentName: name,
        agent: newTaskAgent,
        stopwatchTimer: 0,
        toDo: newTaskStatus === "To do" ? "To do" : "",
        inProgress: newTaskStatus === "In progress" ? "In progress" : "",
        hold: newTaskStatus === "Hold" ? "Hold" : "",
        clean: newTaskStatus === "Clean" ? "Clean" : "",
        createdBy: user?.name || user?.username || "System",
        timestamp: formattedDateTime,
      };

      const res = await fetch("/api/cleaning/save-record", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error("Failed to save task to backend");
      }

      // ✅ Trigger Mattermost API for new task notification
      try {

        const statusEmoji = newTaskStatus === "Clean" ? "✅" : "🔴";
        const statusLabel = newTaskStatus === "Clean" ? "CLEANED" : "TO DO";
        const creatorName = user?.name || user?.username || "System";
        const match = name.match(/\s+\((\d+)\)$/);
        const baseRoomName = match ? name.substring(0, name.length - match[0].length).trim() : name;
        const mattermostMessage = `🧹 **New Cleaning Task Created**\n🏠 Room: **${baseRoomName}**\n📌 Status: ${statusEmoji} **${statusLabel}**\n🕒 Time: **${formattedDateTime}**\n👤 Created by: **${creatorName}**`;

        const payload = { 
          channel_id: process.env.REACT_APP_MATTERMOST_CLEANING_CHANNEL_ID || "96k3rz5uyibkjmja3n47bzfmmh", 
          message: mattermostMessage 
        };

        const mmToken = process.env.REACT_APP_MATTERMOST_CLEANING_TOKEN || "st18ydrda7gd5euqeyotie5h5c";
        const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";

        fetch(mmUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${mmToken}`
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (!response.ok) {
              console.error("❌ Failed to send cleaning task Mattermost notification:", response.status);
            } else {
              console.log("✅ Cleaning task Mattermost notification sent");
            }
          })
          .catch((err) => {
            console.error("❌ Network error sending cleaning task Mattermost notification:", err);
          });
      } catch (mmError) {
        console.error("❌ Error sending Mattermost notification:", mmError);
      }

      setNotification({
        open: true,
        message: `Task "${name}" created successfully!`,
        severity: "success",
      });

      setNewTaskName("");
      setNewTaskAgent("");
      setNewTaskStatus("To do");
      setOpenAddTask(false);

      await fetchApartments();
    } catch (err) {
      console.error(err);
      setNotification({
        open: true,
        message: err.message || "Failed to create task",
        severity: "error",
      });
    } finally {
      setSubmittingTask(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) navigate("/cleaning");
    else if (newValue === 1) navigate("/cleaning-status");
    else if (newValue === 2) navigate("/rooms");
  };

  const isTaskVisibleToday = (apt) => {
    if (!apt || typeof apt !== "object") return false;

    const name = apt.name || "";
    const currentTaskCol = localStorage.getItem(`cleaning_kanban_column_${name}`) || apt.column || "To do";
    const isActiveTask = currentTaskCol === "In progress" || currentTaskCol === "Hold";
    if (isActiveTask) {
      return true; // Always visible while active
    }

    const resId = apt.reservationId ? String(apt.reservationId).trim() : (String(name).match(/\s+\((\d+)\)$/) ? String(name).match(/\s+\((\d+)\)$/)[1] : "");
    const isManualTask = (!resId) && apt.createdBy && String(apt.createdBy).trim() !== "";

    const createdTime = apt.timestampToDo;
    const isCreatedToday = createdTime && isTodayInPakistan(createdTime);

    if (isManualTask) {
      return isCreatedToday;
    }

    // Reservation tasks must have a valid reservation ID
    if (!resId) return false;

    const checkinVal = apt.actualCheckin || "";
    const checkoutVal = apt.actualCheckout || "";
    const hasCheckin = checkinVal.trim() !== "" && checkinVal.trim().toLowerCase() !== "n/a";
    const hasCheckout = checkoutVal.trim() !== "" && checkoutVal.trim().toLowerCase() !== "n/a";

    // Reservation tasks require both check-in and check-out
    if (!hasCheckin || !hasCheckout) return false;

    const isCheckoutToday = apt.actualCheckout && isTodayInPakistan(apt.actualCheckout);
    const isArrivalToday = apt.arrivalDate && isTodayInPakistan(apt.arrivalDate);
    const isDepartureToday = apt.departureDate && isTodayInPakistan(apt.departureDate);

    // Strictly exclude any reservation task from previous or future days
    return isCheckoutToday || isDepartureToday || isArrivalToday;
  };

  const seenRoomResKeys = new Set();
  const filteredApartments = apartments.filter((apt) => {
    if (!isTaskVisibleToday(apt)) return false;

    // Filter by search query
    const matchesSearch = apt.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    // Prevent duplicate tasks with identical reservation ID for the same room
    const resId = apt.reservationId ? String(apt.reservationId).trim() : "";
    const baseName = cleanRoomName(apt.name || "").toLowerCase().trim();
    if (resId) {
      const key = `${baseName}_res_${resId}`;
      if (seenRoomResKeys.has(key)) return false;
      seenRoomResKeys.add(key);
    }

    return true;
  });

  const remainingApartments = allApartments.filter((name) => {
    const getBaseName = (fullName) => {
      if (!fullName) return "";
      const str = String(fullName).trim();
      const match = str.match(/\s+\((\d+)\)$/);
      if (match) {
        return str.substring(0, str.length - match[0].length).trim().toLowerCase();
      }
      return str.toLowerCase();
    };

    const normName = getBaseName(name);

    // Check if there is any task for today for this room
    const hasTodayTask = apartments.some((apt) => {
      if (typeof apt !== "object") return false;
      const aptBaseName = getBaseName(apt.name);
      if (aptBaseName !== normName) return false;

      // Check if task is active/visible today
      return isTaskVisibleToday(apt);
    });

    return !hasTodayTask;
  });

  const checkoutLabelsMap = computeSameDayCheckoutLabels(filteredApartments);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1.5, sm: 3 }} px={{ xs: 1, sm: 1.5, md: 2 }}>

        {/* ── Tab Bar (FDO Panel style) ── */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, sm: 3 } }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            centered
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                mx: { xs: 0.5, sm: 0.5 },
                px: { xs: 1.5, sm: 2, md: 3 },
                py: 1,
                borderRadius: "12px",
                transition: "all 0.3s ease",
                color: "#4b5563",
                backgroundColor: "transparent",
                minWidth: { xs: "55px", sm: "120px" },
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "& .MuiTab-iconWrapper": {
                  marginBottom: 0,
                  marginRight: 0,
                },
              },
              "& .Mui-selected": {
                color: "#000 !important",
                backgroundColor: "transparent !important",
                border: "1.5px solid #000",
                boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Tab
              icon={<CleaningServicesIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Cleaning</Box>}
            />
            <Tab
              icon={<FactCheckIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Cleaning Status</Box>}
            />
            <Tab
              icon={<MeetingRoomIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "28px" }} />}
              label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Rooms</Box>}
            />
          </Tabs>
        </Box>

        {/* ── Table Wrapper Card ── */}
        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          
          {/* Header Controls (Search Box & Add Task button) */}
          <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <TextField
              placeholder="Search Room..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: "280px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  "& fieldset": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                    borderWidth: "1.5px",
                  },
                },
              }}
            />

            <Button
              variant="contained"
              disabled={!canEdit}
              onClick={() => setOpenAddTask(true)}
              startIcon={<Icon>add</Icon>}
              sx={{
                width: { xs: "100%", sm: "auto" },
                backgroundColor: canEdit ? "#000000 !important" : "rgba(0, 0, 0, 0.12) !important",
                color: canEdit ? "#ffffff !important" : "rgba(0, 0, 0, 0.26) !important",
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "10px",
                px: 3,
                py: 1,
                fontSize: "0.85rem",
                pointerEvents: canEdit ? "auto" : "none",
                cursor: canEdit ? "pointer" : "default",
                "&:hover": {
                  backgroundColor: canEdit ? "#1e293b !important" : "rgba(0, 0, 0, 0.12) !important",
                  boxShadow: canEdit ? "0 4px 12px rgba(0,0,0,0.08)" : "none",
                },
              }}
            >
              Add Task
            </Button>
          </Box>

          {/* Table Container */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress color="info" />
              <MDTypography variant="body2" sx={{ ml: 2 }}>Loading rooms...</MDTypography>
            </Box>
          ) : error ? (
            <Box py={4} textAlign="center">
              <MDTypography color="error" variant="h6">Failed to load rooms</MDTypography>
              <MDTypography color="text" variant="body2" sx={{ mt: 1 }}>{error}</MDTypography>
            </Box>
          ) : filteredApartments.length === 0 ? (
            <Box py={8} textAlign="center" sx={{ border: "2px dashed #e2e8f0", borderRadius: "12px" }}>
              <Icon sx={{ fontSize: "40px !important", color: "#94a3b8", mb: 1 }}>search_off</Icon>
              <MDTypography variant="subtitle1" fontWeight="bold" color="text">No Rooms Found</MDTypography>
              <MDTypography variant="caption" color="text">Try adjusting your search query or status filters.</MDTypography>
            </Box>
          ) : isMobile ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {filteredApartments.map((apt, index) => {
                const name = typeof apt === "object" ? apt.name : apt;
                const checkinVal = typeof apt === "object" ? apt.actualCheckin : "";
                const checkoutVal = typeof apt === "object" ? apt.actualCheckout : "";
                const status = localStorage.getItem(`cleaning_kanban_column_${name}`) || (typeof apt === "object" && apt.column) || "To do";
                const checkinEmpty = !checkinVal || checkinVal.trim() === "" || checkinVal.trim().toLowerCase() === "n/a";
                const checkoutEmpty = !checkoutVal || checkoutVal.trim() === "" || checkoutVal.trim().toLowerCase() === "n/a";
                const isAvailable = checkinEmpty || !checkoutEmpty;

                return (
                  <Card
                    key={name}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {/* Header: Room Name and Availability */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.85rem" }}>
                          Room: {index + 1}. {cleanRoomName(name)}
                        </MDTypography>
                        {checkoutLabelsMap.get(apt) && (() => {
                          const cd = checkoutLabelsMap.get(apt);
                          return (
                            <Chip
                              icon={<Icon style={{ fontSize: "12px", color: cd.num === 1 ? "#1d4ed8" : "#6b21a8" }}>{cd.num === 1 ? "schedule" : "history"}</Icon>}
                              label={cd.label}
                              size="small"
                              sx={{
                                background: cd.num === 1 
                                  ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" 
                                  : "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                                color: cd.num === 1 ? "#1e40af" : "#6b21a8",
                                border: `1.5px solid ${cd.num === 1 ? "#93c5fd" : "#c084fc"}`,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                borderRadius: "8px",
                                height: "22px",
                                boxShadow: cd.num === 1 
                                  ? "0 2px 5px rgba(37, 99, 235, 0.12)" 
                                  : "0 2px 5px rgba(168, 85, 247, 0.12)",
                                "& .MuiChip-icon": {
                                  ml: 0.6,
                                  mr: -0.4,
                                },
                              }}
                            />
                          );
                        })()}
                      </Box>
                      <Chip
                        label={isAvailable ? "Available" : "Reserved"}
                        size="small"
                        sx={{
                          backgroundColor: isAvailable ? "#f0fdf4" : "#fef2f2",
                          color: isAvailable ? "#16a34a" : "#dc2626",
                          border: `1.5px solid ${isAvailable ? "#86efac" : "#fca5a5"}`,
                          fontWeight: "bold",
                          fontSize: "0.65rem",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>

                    {/* Task Created Time */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Task Created Time:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                        {formatFullDateTime(apt.timestampToDo || apt.actualCheckout)}
                      </MDTypography>
                    </Box>

                    {/* Task Start Time */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Task Start Time:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                        {formatFullDateTime(apt.timestampInProgress)}
                      </MDTypography>
                    </Box>

                    {/* Task Complete Time */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Task Complete Time:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                        {formatFullDateTime(apt.timestampClean)}
                      </MDTypography>
                    </Box>

                    {/* Cleaning Status */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Cleaning Status:
                      </MDTypography>
                      {renderStatusBadge(status, true)}
                    </Box>

                    {/* Reservation ID */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Reservation ID:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem", fontWeight: "bold" }}>
                        {apt.reservationId || "N/A"}
                      </MDTypography>
                    </Box>

                    {/* Arrival Date */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Arrival Date:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                        {apt.arrivalDate || "N/A"}
                      </MDTypography>
                    </Box>

                    {/* Departure Date */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Departure Date:
                      </MDTypography>
                      <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                        {apt.departureDate || "N/A"}
                      </MDTypography>
                    </Box>

                    {/* Actual Check-In Time */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Actual check in Time:
                      </MDTypography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Icon sx={{ color: "#64748b", fontSize: "12px !important" }}>login</Icon>
                        <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                          {checkinVal || "N/A"}
                        </MDTypography>
                      </Box>
                    </Box>

                    {/* Actual Check-Out Time */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ borderTop: "1px dashed #e2e8f0", pt: 1 }}>
                      <MDTypography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
                        Actual check out Time:
                      </MDTypography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Icon sx={{ color: "#64748b", fontSize: "12px !important" }}>logout</Icon>
                        <MDTypography sx={{ color: "#475569", fontSize: "0.75rem" }}>
                          {checkoutVal || "N/A"}
                        </MDTypography>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <div className="table-responsive" style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <Table striped bordered hover className="mb-0">
                <thead style={{ backgroundColor: "#f8fafc" }}>
                  <tr>
                    <th style={{ width: "70px", textAlign: "center", fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>SR No.</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Room</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Task Created Time</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Task Start Time</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Task Complete Time</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Cleaning Status</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Reservation ID</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Arrival Date</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Departure Date</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Apartment status</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Actual check in time</th>
                    <th style={{ fontWeight: "bold", fontSize: isMobile ? "0.72rem" : "0.8rem", color: "#475569", borderBottom: "2px solid #e2e8f0", padding: headerPadding }}>Actual check out time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApartments.map((apt, index) => {
                    const name = typeof apt === "object" ? apt.name : apt;
                    const checkinVal = typeof apt === "object" ? apt.actualCheckin : "";
                    const checkoutVal = typeof apt === "object" ? apt.actualCheckout : "";
                    const status = localStorage.getItem(`cleaning_kanban_column_${name}`) || (typeof apt === "object" && apt.column) || "To do";
                    const checkinEmpty = !checkinVal || checkinVal.trim() === "" || checkinVal.trim().toLowerCase() === "n/a";
                    const checkoutEmpty = !checkoutVal || checkoutVal.trim() === "" || checkoutVal.trim().toLowerCase() === "n/a";
                    const isAvailable = checkinEmpty || !checkoutEmpty;

                    return (
                      <tr key={name} style={{ verticalAlign: "middle" }}>
                        <td style={{ width: "70px", textAlign: "center", padding: cellPadding }}>
                          <MDTypography variant="subtitle2" sx={{ color: "#475569", fontSize: fontSize }}>
                            {index + 1}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1e293b", fontSize: fontSize }}>
                              {cleanRoomName(name)}
                            </MDTypography>
                            {checkoutLabelsMap.get(apt) && (() => {
                              const cd = checkoutLabelsMap.get(apt);
                              return (
                                <Chip
                                  icon={<Icon style={{ fontSize: "12px", color: cd.num === 1 ? "#1d4ed8" : "#6b21a8" }}>{cd.num === 1 ? "schedule" : "history"}</Icon>}
                                  label={cd.label}
                                  size="small"
                                  sx={{
                                    background: cd.num === 1 
                                      ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" 
                                      : "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                                    color: cd.num === 1 ? "#1e40af" : "#6b21a8",
                                    border: `1.5px solid ${cd.num === 1 ? "#93c5fd" : "#c084fc"}`,
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    borderRadius: "6px",
                                    height: "22px",
                                    boxShadow: cd.num === 1 
                                      ? "0 2px 5px rgba(37, 99, 235, 0.12)" 
                                      : "0 2px 5px rgba(168, 85, 247, 0.12)",
                                    "& .MuiChip-icon": {
                                      ml: 0.6,
                                      mr: -0.4,
                                    },
                                  }}
                                />
                              );
                            })()}
                          </Box>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {formatFullDateTime(apt.timestampToDo || apt.actualCheckout)}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {formatFullDateTime(apt.timestampInProgress)}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {formatFullDateTime(apt.timestampClean)}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          {renderStatusBadge(status, isMobile)}
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {apt.reservationId || "N/A"}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {apt.arrivalDate || "N/A"}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                            {apt.departureDate || "N/A"}
                          </MDTypography>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <Chip
                            label={isAvailable ? "Available" : "Reserved"}
                            size="small"
                            sx={{
                              backgroundColor: isAvailable ? "#f0fdf4" : "#fef2f2",
                              color: isAvailable ? "#16a34a" : "#dc2626",
                              border: `1.5px solid ${isAvailable ? "#86efac" : "#fca5a5"}`,
                              fontWeight: "bold",
                              fontSize: isMobile ? "0.65rem" : "0.72rem",
                              borderRadius: "8px",
                              px: isMobile ? 0.5 : 1,
                            }}
                          />
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ color: "#64748b", fontSize: isMobile ? "12px !important" : "14px !important" }}>login</Icon>
                            <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                              {checkinVal || "N/A"}
                            </MDTypography>
                          </Box>
                        </td>
                        <td style={{ padding: cellPadding }}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ color: "#64748b", fontSize: isMobile ? "12px !important" : "14px !important" }}>logout</Icon>
                            <MDTypography sx={{ color: "#475569", fontSize: fontSize }}>
                              {checkoutVal || "N/A"}
                            </MDTypography>
                          </Box>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

      </MDBox>
      <Footer />

      {/* ── Add Task Dialog ── */}
      <Dialog
        open={openAddTask}
        onClose={() => !submittingTask && setOpenAddTask(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.1rem", pb: 1 }}>
          Create New Cleaning Task
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2, pb: 1 }}>
          <Form.Group controlId="newTaskName">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#344767", marginBottom: "6px" }}>
              Room <span style={{ color: "#dc3545" }}>*</span>
            </Form.Label>
            <Form.Select
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              disabled={submittingTask}
              style={{
                borderRadius: "10px",
                padding: "10px 14px",
                fontSize: "0.875rem",
                borderColor: "#cbd5e1",
                boxShadow: "none",
              }}
            >
              <option value="">Select Room No</option>
              {remainingApartments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <FormControl fullWidth size="small">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#344767", marginBottom: "6px" }}>
              Assign Agent (Optional)
            </Form.Label>
            <Select
              multiple
              value={newTaskAgent ? newTaskAgent.split(", ") : []}
              onChange={(e) => {
                const val = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setNewTaskAgent(val.join(", "));
              }}
              displayEmpty
              disabled={submittingTask}
              renderValue={(selected) =>
                selected && selected.length > 0 ? selected.join(", ") : "None"
              }
              sx={{
                borderRadius: "10px",
                fontSize: "0.875rem",
                "& fieldset": {
                  borderColor: "#cbd5e1",
                },
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  borderRadius: "10px",
                },
              }}
            >
              {agentsList.map((agent) => (
                <MenuItem key={agent} value={agent} sx={{ fontSize: "0.85rem" }}>
                  {agent}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Form.Group controlId="newTaskStatus">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#344767", marginBottom: "6px" }}>
              Initial Status
            </Form.Label>
            <Form.Control
              type="text"
              readOnly
              value="🔴 Not Cleaned"
              style={{
                borderRadius: "10px",
                padding: "10px 14px",
                fontSize: "0.875rem",
                borderColor: "#cbd5e1",
                boxShadow: "none",
                backgroundColor: "#f8fafc",
              }}
            />
          </Form.Group>

          <Form.Group controlId="newTaskTimestamp">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#344767", marginBottom: "6px" }}>
              Timestamp
            </Form.Label>
            <Form.Control
              type="text"
              readOnly
              value={currentDialogTime}
              style={{
                borderRadius: "10px",
                padding: "10px 14px",
                fontSize: "0.875rem",
                borderColor: "#cbd5e1",
                boxShadow: "none",
                backgroundColor: "#f8fafc",
              }}
            />
          </Form.Group>

          <Form.Group controlId="newTaskCreatedBy">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#344767", marginBottom: "6px" }}>
              Created by
            </Form.Label>
            <Form.Control
              type="text"
              readOnly
              value={user?.name || user?.username || "System"}
              style={{
                borderRadius: "10px",
                padding: "10px 14px",
                fontSize: "0.875rem",
                borderColor: "#cbd5e1",
                boxShadow: "none",
                backgroundColor: "#f8fafc",
              }}
            />
          </Form.Group>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenAddTask(false)}
            disabled={submittingTask}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontWeight: "bold",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddTaskSubmit}
            disabled={submittingTask}
            variant="contained"
            sx={{
              backgroundColor: "#000000 !important",
              color: "#ffffff !important",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                backgroundColor: "#1e293b !important",
              },
            }}
          >
            {submittingTask ? <CircularProgress size={20} color="inherit" /> : "Save Task"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CleaningStatus;